import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import XoxodayEnvBadge from '../components/XoxodayEnvBadge';
import { getGiftCardRedemptions } from '../services/giftCardService';
import {
  emitActionNeededCount,
  formatDenomination,
  formatNumber,
  formatUsd,
  getCardOrderedDisplay,
  getEnvDisplay,
  getStatusDisplay,
} from '../constants/giftCard';

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'action_needed', label: 'Action needed' },
  { value: 'sent', label: 'Sent' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'all', label: 'All' },
];

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [1];

  if (current > 3) items.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    items.push(pageNum);
  }

  if (current < total - 2) items.push('…');

  items.push(total);
  return items;
}

export default function GiftCardRedemptions() {
  const [redemptions, setRedemptions] = useState([]);
  const [actionNeededCount, setActionNeededCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('action_needed');
  const navigate = useNavigate();

  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadRedemptions() {
      setLoading(true);
      setError('');

      try {
        const response = await getGiftCardRedemptions({
          status: statusFilter,
          page,
          limit: PAGE_SIZE,
        });

        if (cancelled) return;

        const data = response.data || {};
        setRedemptions(Array.isArray(data.redemptions) ? data.redemptions : []);
        setActionNeededCount(data.actionNeededCount ?? 0);
        emitActionNeededCount(data.actionNeededCount ?? 0);
        setTotalPages(data.pagination?.totalPages || 1);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch gift card redemptions.');
        setRedemptions([]);
        setTotalPages(1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRedemptions();
    return () => {
      cancelled = true;
    };
  }, [page, statusFilter]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page]);

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page || loading) return;
    setPage(nextPage);
  }

  const pageItems = getPageItems(page, totalPages);

  return (
    <div className="payments-page main-bg-top">
      <div className="users-header">
        <div className="gift-card-heading">
          <h2>Gift Card Redemptions</h2>
          <XoxodayEnvBadge />
          {actionNeededCount > 0 && (
            <span className="gift-card-review-count" title="Orders waiting for a decision">
              {actionNeededCount} need{actionNeededCount === 1 ? 's' : ''} action
            </span>
          )}
        </div>
        <div className="users-filters">
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === 'action_needed' && actionNeededCount > 0
                    ? `${option.label} (${actionNeededCount})`
                    : option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="payments-table-scroll" ref={scrollRef}>
        <div className="users-table-wrap">
          <table className="users-table payments-table">
            <thead>
              <tr>
                <th>PO Number</th>
                <th>Buyer</th>
                <th>Product</th>
                <th>Denomination</th>
                <th>USD Value</th>
                <th>Spent</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="users-empty">Loading gift card redemptions...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="9" className="users-empty">{error}</td>
                </tr>
              ) : redemptions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="users-empty">
                    {statusFilter === 'action_needed' ? 'Nothing needs your attention' : 'No gift card redemptions found.'}
                  </td>
                </tr>
              ) : (
                redemptions.map((item) => {
                  const status = getStatusDisplay(item);
                  const env = getEnvDisplay(item.xoxodayEnvironment);
                  const cardOrdered = item.problem ? getCardOrderedDisplay(item.problem.cardOrdered) : null;
                  const name = item.user?.name || '—';
                  const email = item.user?.email || '';

                  return (
                    <tr key={item.id}>
                      <td className="txn-id-cell">{item.poNumber || '—'}</td>
                      <td>
                        <div className="user-cell">
                          <div className="user-cell-avatar">
                            <span className="avatar-placeholder">
                              {name !== '—' ? name.charAt(0).toUpperCase() : '?'}
                            </span>
                          </div>
                          <div className="user-cell-info">
                            <span className="user-cell-name">{name}</span>
                            <span className="user-cell-email">{email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="package-cell">
                          <span className="package-name">{item.productName || '—'}</span>
                          {item.problem?.title ? <span className="gc-problem-line">{item.problem.title}</span> : null}
                          <span className="package-petals">{item.recipientEmail || ''}</span>
                        </div>
                      </td>
                      <td>{formatDenomination(item.denomination, item.currencyCode)}</td>
                      <td>{formatUsd(item.usdValue)}</td>
                      <td>
                        <div className="package-cell">
                          <span className="package-name">{formatNumber(item.coinsSpent)} Coins</span>
                          <span className="package-petals">{formatNumber(item.petalsSpent)} Petals</span>
                        </div>
                      </td>
                      <td>
                        <div className="gc-status-cell">
                          <span className={`status-badge status-${status.className}`}>{status.label}</span>
                          {cardOrdered && (
                            <span className={`status-badge status-${cardOrdered.className}`} title={cardOrdered.label}>
                              {cardOrdered.short}
                            </span>
                          )}
                          {env && <span className={`gc-env-badge ${env.className}`}>{env.label}</span>}
                        </div>
                      </td>
                      <td>
                        <div className="datetime-cell">
                          <span className="datetime-date">{formatDate(item.createdAt)}</span>
                          <span className="datetime-time">{formatTime(item.createdAt)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button
                            className="action-btn"
                            title="View"
                            type="button"
                            onClick={() => navigate(`/gift-card-redemptions/${item.id}`)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="payments-pagination" aria-label="Pagination">
          <button
            type="button"
            className="pagination-page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          {pageItems.map((item, index) =>
            item === '…' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`pagination-page-btn ${page === item ? 'active' : ''}`}
                onClick={() => goToPage(item)}
                aria-current={page === item ? 'page' : undefined}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className="pagination-page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
